import { computed, inject, ref, type InjectionKey, type Ref } from 'vue'
import { createSurfaceFormatters, type SurfaceFormatOptions } from './format'
import { DEFAULT_CULTURE_BY_LANGUAGE, isSurfaceLanguage, normalizeCulture, type SurfaceLanguage } from './languages'
import { languageCookie, localeCookieOptionsFromEnv, type LanguageCookieOptions, type LocaleCookieEnvironment, type LocalePreference, type LocaleSelection } from './languageCookie'
import { resolveCulture, resolveLanguage } from './resolve'

declare global {
  interface ImportMetaEnv {
    readonly VITE_LOCALE_COOKIE_NAME?: string
    readonly VITE_LOCALE_COOKIE_DOMAIN?: string
    readonly VITE_LOCALE_COOKIE_SECURE?: string
  }
  interface ImportMeta { readonly env: ImportMetaEnv }
}

export type SurfaceLocaleOptions = { readonly mode: 'fixed' } | {
  readonly mode: 'resolved'
  readonly cookie?: LanguageCookieOptions
  /** Synchronous snapshot of an already loaded preference; FE-L7 owns server integration. */
  readonly preference?: () => LocalePreference | undefined
  readonly onPersist?: (selection: LocaleSelection) => void | Promise<void>
}

export interface SurfaceLocale {
  readonly language: Readonly<Ref<SurfaceLanguage>>
  readonly culture: Readonly<Ref<string>>
  /** False in fixed mode or if persistence fails; a failed hook never rolls back local state. */
  setLanguage(language: SurfaceLanguage): Promise<boolean>
  /** Undefined restores automatic culture selection. Same persistence semantics as setLanguage. */
  setCulture(culture?: string): Promise<boolean>
}

export const SURFACE_LOCALE_KEY: InjectionKey<SurfaceLocale> = Symbol('SurfaceLocale')

/** Per-app state; the fixed branch does not read browser hints or write any browser state. */
export function createSurfaceLocale(composerLocale: Ref<string>, options?: SurfaceLocaleOptions): SurfaceLocale {
  const language = computed(() => isSurfaceLanguage(composerLocale.value) ? composerLocale.value : 'tr')
  if (options?.mode !== 'resolved') {
    return {
      language,
      culture: computed(() => DEFAULT_CULTURE_BY_LANGUAGE[language.value]),
      setLanguage: async () => false,
      setCulture: async () => false,
    }
  }

  const buildEnv: LocaleCookieEnvironment = {
    VITE_LOCALE_COOKIE_NAME: import.meta.env.VITE_LOCALE_COOKIE_NAME,
    VITE_LOCALE_COOKIE_DOMAIN: import.meta.env.VITE_LOCALE_COOKIE_DOMAIN,
    VITE_LOCALE_COOKIE_SECURE: import.meta.env.VITE_LOCALE_COOKIE_SECURE,
  }
  const cookie = languageCookie({ ...localeCookieOptionsFromEnv(buildEnv), ...options.cookie })
  const hint = cookie.read()
  const preference = options.preference?.()
  const navigatorLanguages = typeof navigator === 'undefined' ? [] : [...(navigator.languages ?? [navigator.language])]
  composerLocale.value = resolveLanguage({ preference: preference?.language, cookie: hint.language, navigatorLanguages, fallback: 'tr' })
  // Use a ref so all consumers react when the explicit culture override is cleared or replaced.
  const selectedCulture = ref(preference ? normalizeCulture(preference.culture) : normalizeCulture(hint.culture))
  const culture = computed(() => resolveCulture({ language: language.value, preferenceCulture: selectedCulture.value, navigatorLanguages }))
  updateDocumentLanguage(language.value)
  const onPersist = options.onPersist

  async function persist(): Promise<boolean> {
    const selection = { language: language.value, culture: selectedCulture.value }
    let cookiePersisted = true
    try {
      cookie.write(selection)
    } catch {
      // The cookie adapter reports the error; still attempt account persistence.
      cookiePersisted = false
    }
    try {
      await onPersist?.(selection)
      return cookiePersisted
    } catch {
      // The returned status lets the caller report/retry failure without reverting the UI.
      return false
    }
  }

  return {
    language,
    culture,
    async setLanguage(value) {
      if (!isSurfaceLanguage(value)) throw new RangeError('Unsupported surface language')
      composerLocale.value = value
      updateDocumentLanguage(value)
      return persist()
    },
    async setCulture(value) {
      const normalized = normalizeCulture(value)
      if (value !== undefined && !normalized) throw new RangeError('Invalid surface culture')
      selectedCulture.value = normalized
      return persist()
    },
  }
}

function updateDocumentLanguage(language: SurfaceLanguage): void {
  if (typeof document !== 'undefined') document.documentElement.lang = language
}

export function useSurfaceLocale(): SurfaceLocale {
  const locale = inject(SURFACE_LOCALE_KEY)
  if (!locale) throw new Error('SurfaceLocale is not provided; call useSurfaceLocale inside a createSurfaceApp tree')
  return locale
}

export function useSurfaceFormatters(options: SurfaceFormatOptions) {
  const { culture } = useSurfaceLocale()
  return createSurfaceFormatters(() => culture.value, options)
}
